/**
 * XHR-based upload with real progress events.
 *
 * `fetch()` does NOT expose upload progress — once you send a 30 MB body,
 * the user stares at a frozen "Submitting…" spinner for 60+ seconds with
 * no idea whether anything is happening. Many users assume it's broken,
 * abandon the page, refresh, or close the tab → "Failed to fetch".
 *
 * XHR's `upload.onprogress` solves this. Use the same API surface as
 * fetch but report progress via the `onProgress` callback.
 */
export function uploadWithProgress(url, { method = 'POST', body, headers = {}, credentials = 'omit', onProgress } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    xhr.withCredentials = credentials === 'include';
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));

    xhr.upload.onprogress = (evt) => {
      if (!onProgress) return;
      if (evt.lengthComputable) {
        onProgress({ loaded: evt.loaded, total: evt.total, pct: Math.round((evt.loaded / evt.total) * 100) });
      } else {
        onProgress({ loaded: evt.loaded, total: 0, pct: null });
      }
    };
    xhr.onload = () => {
      // Build a fetch-like response so callers can use res.ok / res.json().
      const ok = xhr.status >= 200 && xhr.status < 300;
      let parsed = null;
      try { parsed = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch (_) { /* non-json */ }
      resolve({
        ok,
        status: xhr.status,
        statusText: xhr.statusText,
        json: async () => parsed ?? {},
        text: async () => xhr.responseText,
      });
    };
    xhr.onerror = () => reject(new TypeError('Failed to fetch'));
    xhr.ontimeout = () => reject(new TypeError('Failed to fetch'));
    // Hard timeout 5 min — gives slow connections breathing room without
    // hanging forever if the server is genuinely down.
    xhr.timeout = 5 * 60 * 1000;
    xhr.send(body);
  });
}
